<template>
	<div v-if="queue" :class="{'is-edit-mode': editMode, 'has-editable-items': hasEditableItems}" class="job-queue">

		<!-- Find Help page - ranked -->
		<draggable v-if="context == 'findHelpQueue' && queueType == 'ranked'"
			v-model="editableItems"
			group="job-queue"
			tag="ol"
			class="job-queue-candidates is-editable grid gap-16 is-draggable"
			@change="onReorder"
			@start="drag = true"
			@end="drag = false">
			<JobQueueCandidate v-for="(profile, index) of editableItems"
				:key="`${profile.userId}${index}`"
				:context="context"
				v-bind="getCandidateProps(profile, index + readonlyItems.length)"
				@remove="onRemove($event)" />
		</draggable>
		
		<!-- Find Help page - instant queue -->
		<ol v-if="context == 'findHelpQueue' && queueType == 'instant'" class="job-queue-candidates grid gap-16 is-editable">
			<JobQueueCandidate v-for="(profile, index) of editableItems"
				:key="`${profile.userId}${index}`"
				:inn="index"
				v-bind="getCandidateProps(profile, index + readonlyItems.length)"
				@remove="onRemove($event)"
				/>
		</ol>

		<!-- Job page - Ranked queue - Active candidates -->
		<draggable v-if="context == 'activeRankedOffer' && hasEditableItems"
			v-model="editableItems"
			group="job-queue"
			tag="ol"
			class="job-queue-candidates is-editable grid gap-16 is-draggable"
			@change="onReorder"
			@start="drag = true"
			@end="drag = false">
			<JobQueueCandidate v-for="(profile, index) of editableItems"
				:key="`${profile.userId}${index}`"
				:context="context"
				v-bind="getCandidateProps(profile, index + readonlyItems.length)"
				@remove="onRemove($event)" />
		</draggable>

		<!-- Job page - Completed job - All candidates -->
		<ol v-else-if="context == 'completed'" class="job-queue-candidates is-editable grid gap-16">
			<JobQueueCandidate v-for="(profile, index) of readonlyItems"
				:key="`${profile.userId}${index}`"
				v-bind="getCandidateProps(profile, index)"
				context="singleJobInactive"
				:class="context"
				/>
		</ol>

		<slot name="after" v-bind="slotScope" />
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import JobQueueCandidate from '@/components/jobs/JobQueueCandidate.vue';
import { JobQueue } from '@/models/Job';

export default {
  name: 'JobQueue',
  components: { draggable, JobQueueCandidate },
  model: {
    prop: 'queue',
    event: 'change',
  },
  props: {
    canRemoveCandidates: {
      type: Boolean,
      default: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    queue: {
      type: JobQueue,
      required: true,
    },
	queueType: {
		type: String,
	},
	context: {
		type: String,
	},
  },
  data() {
    return {
      declinedCandidates: [],
      activeInstantOfferCandidates: [],
      editableItems: [],
      emittingChange: false,
      items: [],
      readonlyItems: [],
      version: 0,
    };
  },
  computed: {

	activeInstantOfferQueueCandidates() {
		return this.queue.candidates.filter(obj => ['awaitingResponse', 'inQueue'].includes(obj.status));
	},
    hasEditableItems() {
      return !!this.editMode && this.editableItems.length > 0;
    },
    slotScope() {
      return {
        getLabel: this.getLabel.bind(this),
        items: this.items,
      };
    },
  },
  methods: {
    getCandidateProps(profile, index) {
      let candidate;
      let queue;
      if (
        !profile ||
        !(queue = this.queue) ||
        !(candidate = queue.candidates[index])
      ) {
        return null;
      }

      return {
        canRemoveCandidates: this.canRemoveCandidates,
        candidate,
        editMode: this.editMode,
        label: this.getLabel(index),
        profile,
        queue,
		queueType: this.queueType,
        tag: 'li',
      };
    },
    getLabel(index) {
      return `${String(index + 1).padStart(2, '0')}`;
    },
    async loadItems() {
      const queue = this.queue;

      if (!queue || this.emittingChange) {
        return;
      }
      const candidates = queue.candidates;
      const version = ++this.version;
      
      const profileMap = await this.$store.dispatch(
        'profiles/getProfiles',
        candidates.map((candidate) => candidate.userId)
      );
      if (version !== this.version) {
        return;
      }
      /** @type {Profile[]} */
      const items = [];
      let index = 0;
      let profile;
      let userId;
      for (const candidate of candidates) {
        if (candidate && (userId = candidate.userId) && (profile = profileMap.get(userId))) {
          items[index++] = profile;
        }
      }
      this.items = items;
		// loop through and add declined + expired items to declinedCandidates
		candidates.forEach((candidate, i) => {
			if (['declined', 'expired', 'declinedAfterAccepted', 'declinedAfterAcceptedChanges'].includes(candidate.status)) {
				const profile = items[i];
				items[i].queueIndex = i;
				profile.status = candidate.status;
				this.declinedCandidates.push(items[i]);
			}


			// Instant queue - candidate booked then declined
			if (this.queue.type == 'instant' && this.queue.status == 'matchingFailed') {
				if (['bePreempted', 'inQueue'].includes(candidate.status)) {
					const profile = items[i];
					items[i].queueIndex = i;
					profile.status = candidate.status;
					this.activeInstantOfferCandidates.push(items[i]);
				}
			}
			
			// Instant queue - running
			if (this.queue.type == 'instant' && this.queue.status == 'running') {
				if (candidate.status == 'awaitingResponse') {
					const profile = items[i];
					items[i].queueIndex = i;
					profile.status = candidate.status;
					this.activeInstantOfferCandidates.push(items[i]);
				}
			}
		})
		
      /**
       * When editing a newly created queue (wherein `queue.id == null`), the first editable
       * index is always 0. Otherwise, it's the index of the first candidate whose status is
       * "inQueue".
       */
      const firstEditableIndex = this.editMode
        ? queue.id
          ? candidates.findIndex(({ status }) => status === 'inQueue')
          : 0
        : -1;
      if (firstEditableIndex >= 0) {
        this.editableItems = items.slice(firstEditableIndex);
        this.readonlyItems = items.slice(0, firstEditableIndex);
	} else {
		this.editableItems = [];
        this.readonlyItems = items.slice();
      }
    },
    /**
     * @param {UserId} userId
     * @returns {void}
     */
    onRemove(userId) {
      const { editableItems, items, queue } = this;
      if (!queue) return;
      let index;
      let removedCandidate = null;
      let removedProfile = null;

	// If userID matches item in 'editableItems'
      if ((index = editableItems.findIndex((item) => item.userId === userId)) >= 0) {
        // Remove it
		removedProfile = editableItems[index] || null;
        editableItems.splice(index, 1);

		// Do the same for 'items' array
        if ((index = items.findIndex((item) => item.userId === userId)) >= 0) {
          if (!removedProfile) {
            removedProfile = items[index] || null;
          }
          items.splice(index, 1);
        }
      }

	// Remove from 'candidates'
      const { candidates } = queue;
      if ((index = candidates.findIndex((candidate) => candidate.userId === userId)) >= 0) {
        removedCandidate = candidates[index] || null;
        candidates.splice(index, 1);
      }


      if (removedCandidate || removedProfile) {
		const emitPayload = this.context == 'activeRankedOffer'
			? editableItems
			: { candidate: removedCandidate, profile: removedProfile };
        this.$emit('remove', emitPayload);
      }
    },
    /**
     * @returns {void}
     */
    onReorder() {
      const queue = this.queue;
      if (!queue) return;

      this.emittingChange = true;
      try {
        const candidateMap = new Map(
          queue.candidates.map((candidate) => [candidate.userId, candidate])
        );
        const candidates = [];
        let candidate;
        let index = 0;
        for (const item of this.readonlyItems.concat(this.editableItems)) {
          if ((candidate = candidateMap.get(item.userId))) {
            candidates[index++] = candidate;
          }
        }
        queue.candidates = candidates;
		
		if (this.context == 'activeRankedOffer') {
			this.$emit('reorder', this.editableItems);
		} else {
			this.$emit('change', queue);
		}
      } finally {
        this.emittingChange = false;
      }
    },
  },
  watch: {
    candidates: 'loadItems',
    'queue.candidates': {
      handler() {
        this.loadItems();
      },
      immediate: true,
    },
  },
};
</script>
